import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Trans, useTranslation } from "react-i18next";
import { FormControl, FormGroup, FormLabel } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { FiCalendar, FiInfo, FiSearch } from "react-icons/fi";
import { toastr } from "react-redux-toastr";
import { accountsService, employeesService, groupsService, plansService } from "services";
import { errorFormating, formatNumber, getContrastColor } from "utils/utils";
import { resolveDateLocale } from "utils/dateConfig";
import Avatar from "components/Avatar";
import SubmitButton from "components/SubmitButton";
import {
	BALANCE_START,
	COVERAGE_DAY_OPTIONS,
	TEMPORARY_EMPLOYEES_PAGE_SIZE,
	balanceTone,
	buildShiftDisplay,
	coverageEndDay,
	fetchBalanceByEmployee,
	formatCoverageStartLabel,
	getDayAssignments,
	employeeMatchesFunction,
	getEmployeeFunctionName,
	getTotalItems,
	originGroupsForCoverage,
	rowHasBlockingShift,
	unwrapAssignmentRows,
	weekBounds,
	weekHoursLine,
} from "./temporaryGroupEmployees";
import "react-datepicker/dist/react-datepicker.css";
import "./TemporaryGroupEmployeesModal.scss";

const formatSignedHours = (value) => {
	const parsed = Number(value) || 0;
	const sign = parsed >= 0 ? "+" : "-";
	return `${sign}${formatNumber(Math.abs(parsed))} h`;
};

const employeeName = (employee) =>
	`${employee?.name || ""} ${employee?.surname || ""}`.trim();

const ShiftChip = ({ assignment }) => {
	const { t } = useTranslation();
	const display = buildShiftDisplay(assignment);
	if (!display) return null;

	const color = display.background ? getContrastColor(display.background) : "#55536b";
	const style = {
		backgroundColor: display.background || "#f3f4f6",
		borderColor: display.border || "transparent",
		color,
	};

	if (!display.hoursLayout) {
		return (
			<div className="temporary-employees__shift is-label" style={style} title={display.name}>
				{display.rest ? t("shifts.form.typeRest") : display.name}
			</div>
		);
	}

	const functionStyle = display.functionColor
		? {
				backgroundColor: display.functionColor,
				borderColor: display.functionColor,
				color: getContrastColor(display.functionColor),
		  }
		: undefined;

	return (
		<div className="temporary-employees__shift" style={style} title={display.name}>
			<div className="temporary-employees__shift-main">
				<ul className="temporary-employees__intervals">
					{display.intervals.map((interval) => (
						<li key={`${interval.start}-${interval.end}`}>
							{interval.start}-{interval.end}h
						</li>
					))}
				</ul>
				{display.total ? (
					<span className="temporary-employees__shift-total">{display.total}</span>
				) : null}
			</div>
			<div className="temporary-employees__shift-side">
				{display.functionName ? (
					<span className="temporary-employees__shift-function" style={functionStyle}>
						{display.functionName}
					</span>
				) : null}
				{display.badge ? (
					<span className="temporary-employees__shift-badge">{display.badge}</span>
				) : null}
			</div>
		</div>
	);
};

const TemporaryGroupEmployeesModal = ({
	functionId,
	functionName,
	groupId,
	openDay,
	onAssigned,
	handleClose,
}) => {
	const { t, i18n } = useTranslation();
	const dateLocale = resolveDateLocale(i18n.language);
	const openedDay = moment(openDay).format("YYYY-MM-DD");

	const [coverageDays, setCoverageDays] = useState(1);
	const [dayOffset, setDayOffset] = useState(0);
	const [rows, setRows] = useState([]);
	const [balances, setBalances] = useState({});
	const [targets, setTargets] = useState({});
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [selected, setSelected] = useState({});
	const [startDay, setStartDay] = useState(openedDay);
	const [originGroups, setOriginGroups] = useState([]);
	const [groupsReady, setGroupsReady] = useState(false);
	const [groupFilter, setGroupFilter] = useState("");
	const [query, setQuery] = useState("");
	const [sort, setSort] = useState({ key: "name", dir: "asc" });
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState([]);
	const requestIdRef = useRef(0);

	const viewDay = moment(startDay).add(dayOffset, "day").format("YYYY-MM-DD");
	const endDay = coverageEndDay(startDay, coverageDays);

	const load = useCallback(async () => {
		if (!functionId || !groupFilter) return;
		const requestId = requestIdRef.current + 1;
		requestIdRef.current = requestId;
		setLoading(true);
		setLoadError(false);

		const week = weekBounds(viewDay);
		const originGroupId = Number(groupFilter);

		try {
			const collected = [];
			let page = 0;
			let total = null;

			while (page < 50) {
				const plansResponse = await plansService.getPlanAssignments({
					page,
					page_size: TEMPORARY_EMPLOYEES_PAGE_SIZE,
					start: viewDay,
					end: viewDay,
					group_id: originGroupId,
					function_id: functionId,
					dont_filter_plans_by_function: true,
				});
				if (requestIdRef.current !== requestId) return;
				if (plansResponse?.ok === false) {
					setLoadError(true);
					setRows([]);
					return;
				}
				const chunk = unwrapAssignmentRows(plansResponse);
				collected.push(...chunk);
				const reported = getTotalItems(plansResponse, collected.length);
				if (reported) total = reported;
				if (chunk.length < TEMPORARY_EMPLOYEES_PAGE_SIZE) break;
				if (total && collected.length >= total) break;
				page += 1;
			}

			const [balanceIndex, targetIndex] = await Promise.all([
				fetchBalanceByEmployee(accountsService, {
					functionId,
					groupId: originGroupId,
					start: week.start,
					end: week.end,
				}),
				fetchBalanceByEmployee(accountsService, {
					functionId,
					groupId: originGroupId,
					start: week.start,
					end: week.end,
					accountSystemId: 4,
					transactionRuleSystemId: undefined,
				}),
			]);

			if (requestIdRef.current !== requestId) return;
			setRows(collected);
			setBalances(balanceIndex || {});
			setTargets(targetIndex || {});
		} catch (error) {
			if (requestIdRef.current !== requestId) return;
			setLoadError(true);
			setRows([]);
		} finally {
			if (requestIdRef.current === requestId) setLoading(false);
		}
	}, [functionId, groupFilter, viewDay]);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			try {
				const response = await groupsService.getAll({
					variant: "select",
					sort: "+name",
				});
				if (cancelled) return;
				if (response?.ok === false) {
					setLoadError(true);
					setOriginGroups([]);
					setGroupFilter("");
					setLoading(false);
					return;
				}
				const raw = Array.isArray(response?.data)
					? response.data
					: Array.isArray(response)
						? response
						: [];
				const list = originGroupsForCoverage(raw, groupId);
				setOriginGroups(list);
				setGroupFilter(list[0] ? String(list[0].id) : "");
				if (!list[0]) setLoading(false);
			} catch (error) {
				if (cancelled) return;
				setLoadError(true);
				setOriginGroups([]);
				setGroupFilter("");
				setLoading(false);
			} finally {
				if (!cancelled) setGroupsReady(true);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [groupId]);

	useEffect(() => {
		load();
	}, [load]);

	const functionRows = useMemo(
		() => rows.filter((row) => employeeMatchesFunction(row.employee, functionId)),
		[rows, functionId]
	);

	const visibleRows = useMemo(() => {
		const needle = query.trim().toLowerCase();
		const filtered = functionRows.filter((row) => {
			if (!needle) return true;
			return employeeName(row.employee).toLowerCase().includes(needle);
		});

		const direction = sort.dir === "asc" ? 1 : -1;
		filtered.sort((a, b) => {
			if (sort.key === "balance") {
				const left = Number(balances[a.employee?.id]) || 0;
				const right = Number(balances[b.employee?.id]) || 0;
				return (left - right) * direction;
			}
			if (sort.key === "shift") {
				const left = getDayAssignments(a, viewDay)
					.map((item) => item.shift?.name || "")
					.join(" ");
				const right = getDayAssignments(b, viewDay)
					.map((item) => item.shift?.name || "")
					.join(" ");
				return left.localeCompare(right, "es") * direction;
			}
			return (
				employeeName(a.employee).localeCompare(employeeName(b.employee), "es") *
				direction
			);
		});
		return filtered;
	}, [balances, functionRows, query, sort, viewDay]);

	const selectedCount = Object.keys(selected).length;

	const toggleEmployee = (employee) => {
		setSelected((current) => {
			const next = { ...current };
			if (next[employee.id]) delete next[employee.id];
			else next[employee.id] = employeeName(employee);
			return next;
		});
	};

	const toggleSort = (key) => {
		setSort((current) =>
			current.key === key
				? { key, dir: current.dir === "asc" ? "desc" : "asc" }
				: { key, dir: "asc" }
		);
	};

	const changeCoverageDays = (days) => {
		setCoverageDays(days);
		setDayOffset((current) => Math.min(current, days - 1));
	};

	const changeStartDay = (date) => {
		setStartDay(date ? moment(date).format("YYYY-MM-DD") : openedDay);
		setDayOffset(0);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (selectedCount === 0 || submitting || !startDay || !endDay) return;

		setSubmitting(true);
		setErrors([]);
		const failures = [];
		let successful = 0;

		for (const [id, name] of Object.entries(selected)) {
			const response = await employeesService.addGroup(id, {
				group_id: groupId,
				employee_id: Number(id),
				start_day: startDay,
				end_day: endDay,
			});

			if (!response?.ok) {
				const formatted = errorFormating(response);
				const message = Object.values(formatted).filter(Boolean).join(", ");
				failures.push(`${name}: ${message || t("common.errorGeneric")}`);
			} else {
				successful += 1;
			}
		}

		setSubmitting(false);

		if (failures.length === 0) {
			toastr.success(
				t("common.success"),
				selectedCount === 1
					? t("plan.temporaryEmployees.assignedOne")
					: t("plan.temporaryEmployees.assignedMany")
			);
			if (onAssigned) onAssigned();
			handleClose();
			return;
		}

		setErrors(failures);
		if (successful > 0 && onAssigned) onAssigned();
	};

	const availabilityDay = moment(viewDay).locale(dateLocale).format("dddd, D [de] MMMM [de] YYYY");
	const availabilityLabel =
		availabilityDay.charAt(0).toUpperCase() + availabilityDay.slice(1);
	const sortMark = (key) => (sort.key === key ? (sort.dir === "asc" ? " ↑" : " ↓") : "");
	const submitLabel =
		selectedCount === 0
			? t("plan.temporaryEmployees.selectOne")
			: selectedCount === 1
				? t("plan.temporaryEmployees.assignOne")
				: t("plan.temporaryEmployees.assignMany", { count: selectedCount });

	return (
		<form onSubmit={handleSubmit} className="temporary-employees">
			<div className="modal-body">
				<p className="info-text temporary-employees__intro">
					<FiInfo className="info-icon" aria-hidden="true" />
					<span>
						<Trans
							i18nKey="plan.temporaryEmployees.intro"
							values={{ functionName }}
						/>
					</span>
				</p>

				<div className="temporary-employees__period">
					<div className="temporary-employees__start">
						<span>{t("plan.temporaryEmployees.coverageStart")}</span>
						<DatePicker
							selected={startDay ? moment(startDay).toDate() : null}
							onChange={changeStartDay}
							dateFormat={dateLocale === "en" ? "MMMM d, yyyy" : "d 'de' MMMM 'de' yyyy"}
							locale={dateLocale}
							minDate={moment(BALANCE_START).toDate()}
							customInput={
								<button type="button" className="temporary-employees__start-btn">
									{formatCoverageStartLabel(startDay, dateLocale)}
									<FiCalendar />
								</button>
							}
						/>
					</div>
					<div className="temporary-employees__duration" role="radiogroup">
						{COVERAGE_DAY_OPTIONS.map((days) => {
							const active = coverageDays === days;
							return (
								<label
									key={days}
									className={`temporary-employees__day-option${active ? " is-active" : ""}`}
								>
									<input
										type="radio"
										name="coverage-days"
										checked={active}
										onChange={() => changeCoverageDays(days)}
									/>
									<span className="temporary-employees__day-dot" />
									<span>{t("plan.temporaryEmployees.days", { count: days })}</span>
								</label>
							);
						})}
					</div>
				</div>

				<div className="temporary-employees__filters">
					<FormGroup className="form-group temporary-employees__group" controlId="temporary-origin-group">
						<FormLabel>{t("plan.temporaryEmployees.originGroup")}</FormLabel>
						<FormControl
							as="select"
							value={groupFilter}
							onChange={(event) => setGroupFilter(event.target.value)}
							disabled={!groupsReady || originGroups.length === 0}
						>
							{originGroups.map((group) => (
								<option key={group.id} value={String(group.id)}>
									{group.name}
								</option>
							))}
						</FormControl>
					</FormGroup>
					<label className="temporary-employees__search">
						<FiSearch />
						<input
							type="search"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder={t("plan.temporaryEmployees.searchEmployee")}
						/>
					</label>
				</div>

				<div className="temporary-employees__availability">
					<span>
						<span className="temporary-employees__availability-prefix">
							{t("plan.temporaryEmployees.availabilityPrefix")}
						</span>
						<strong>{availabilityLabel}</strong>
					</span>
					<div className="temporary-employees__day-pager">
						<button
							type="button"
							disabled={dayOffset === 0}
							onClick={() => setDayOffset((current) => Math.max(0, current - 1))}
							aria-label={t("plan.temporaryEmployees.previousDay")}
						>
							‹
						</button>
						<span>
							{t("plan.temporaryEmployees.dayOf", {
								current: dayOffset + 1,
								total: coverageDays,
							})}
						</span>
						<button
							type="button"
							disabled={dayOffset + 1 >= coverageDays}
							onClick={() =>
								setDayOffset((current) => Math.min(coverageDays - 1, current + 1))
							}
							aria-label={t("plan.temporaryEmployees.nextDay")}
						>
							›
						</button>
					</div>
				</div>

				{!groupsReady || loading ? (
					<p className="temporary-employees__status">{t("common.loading")}</p>
				) : loadError ? (
					<p className="temporary-employees__status is-error">
						{t("plan.temporaryEmployees.loadError")}
					</p>
				) : originGroups.length === 0 ? (
					<p className="temporary-employees__status">
						{t("plan.temporaryEmployees.noOriginGroups")}
					</p>
				) : visibleRows.length === 0 ? (
					<p className="temporary-employees__status">
						{t("plan.temporaryEmployees.empty")}
					</p>
				) : (
					<div className="temporary-employees__table-wrap">
						<table className="temporary-employees__table">
							<thead>
								<tr>
									<th className="is-check" />
									<th className="is-employee">
										<button type="button" onClick={() => toggleSort("name")}>
											{t("common.employee")}
											{sortMark("name")}
										</button>
									</th>
									<th className="is-shift">
										<button type="button" onClick={() => toggleSort("shift")}>
											{t("plan.temporaryEmployees.shiftColumn")}
											{sortMark("shift")}
										</button>
									</th>
									<th className="is-balance">
										<button type="button" onClick={() => toggleSort("balance")}>
											{t("plan.temporaryEmployees.weekBalance")}
											{sortMark("balance")}
										</button>
									</th>
								</tr>
							</thead>
							<tbody>
								{visibleRows.map((row) => {
									const employee = row.employee || {};
									const name = employeeName(employee);
									const functionLabel = getEmployeeFunctionName(employee);
									const shifts = getDayAssignments(row, viewDay);
									const checked = Boolean(selected[employee.id]);
									const blocked = rowHasBlockingShift(row, viewDay);
									const locked = blocked && !checked;
									const balance = balances[employee.id];
									const line = weekHoursLine(balance, targets[employee.id]);

									return (
										<tr
											key={employee.id}
											className={locked ? "is-blocked" : undefined}
											title={
												locked ? t("plan.temporaryEmployees.blockedShift") : undefined
											}
											onClick={() => {
												if (locked) return;
												toggleEmployee(employee);
											}}
										>
											<td className="is-check">
												<input
													type="checkbox"
													checked={checked}
													disabled={locked}
													onClick={(event) => event.stopPropagation()}
													onChange={() => {
														if (locked) return;
														toggleEmployee(employee);
													}}
													aria-label={name}
												/>
											</td>
											<td className="is-employee">
												<div className="temporary-employees__person">
													<Avatar
														userId={employee.id}
														className="avatar-xs"
														alt={name}
														name={name}
													/>
													<div>
														<strong>{name}</strong>
														{functionLabel ? <span>{functionLabel}</span> : null}
													</div>
												</div>
											</td>
											<td className="is-shift">
												{shifts.length > 0 ? (
													<div className="temporary-employees__shifts">
														{shifts.map((item) => (
															<ShiftChip key={item.id} assignment={item} />
														))}
													</div>
												) : (
													<span className="temporary-employees__rest">
														{t("shifts.form.typeRest")}
													</span>
												)}
											</td>
											<td className="is-balance">
												{balance === undefined ? (
													"—"
												) : (
													<div className={`temporary-employees__balance is-${balanceTone(balance)}`}>
														<strong>{formatSignedHours(balance)}</strong>
														{line ? (
															<span>
																{formatNumber(line.hours)} / {formatNumber(line.target)} h
															</span>
														) : null}
													</div>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}

				{errors.length > 0 && (
					<ul className="temporary-employees__errors">
						{errors.map((message) => (
							<li key={message}>{message}</li>
						))}
					</ul>
				)}
			</div>

			<div className="modal-footer temporary-employees__footer">
				<span>
					{selectedCount === 0
						? t("plan.temporaryEmployees.noneSelected")
						: t("plan.temporaryEmployees.selectedCount", { count: selectedCount })}
				</span>
				<SubmitButton
					type="submit"
					isLoading={submitting}
					disabled={selectedCount === 0 || !startDay || !endDay}
					text={submitLabel}
					loadingText={t("plan.temporaryEmployees.bringing")}
				/>
			</div>
		</form>
	);
};

export default TemporaryGroupEmployeesModal;
